import type { PetCustomAlert, PetReservationSummary } from "./usePetReservationSummary";
import type { ReservationApplication } from "./petProfileModel";

export type PetProfileReadChip =
  | { kind: "application-overdue" | "application-upcoming"; application: ReservationApplication }
  | { kind: "alert"; id: string; text: string; color: string | null }
  | { kind: "allergy" | "temperament"; names: string[] };

export type PetProfileReadModel = {
  chips: PetProfileReadChip[];
  alerts: Extract<PetProfileReadChip, { kind: "alert" }>[];
  notes: string | null;
  relationships: PetReservationSummary["relationships"];
  storedItems: PetReservationSummary["storedItems"];
};

const GENERIC_ALERT_LABELS = new Set(["alerta", "alert"]);

export function getPetAlertText(alert: Pick<PetCustomAlert, "label" | "value">) {
  const value = alert.value.trim();
  if (value) return value;
  const label = alert.label.trim();
  return label && !GENERIC_ALERT_LABELS.has(label.toLocaleLowerCase()) ? label : null;
}

export function buildPetProfileReadModel(summary: PetReservationSummary): PetProfileReadModel {
  const overdue = summary.applications
    .filter((application) => application.status === "overdue")
    .map((application) => ({ kind: "application-overdue" as const, application }));
  const alerts = summary.alerts.flatMap((alert) => {
    const text = getPetAlertText(alert);
    return text ? [{ kind: "alert" as const, id: alert.id, text, color: alert.color }] : [];
  });
  const allergies = summary.allergies.length
    ? [{ kind: "allergy" as const, names: summary.allergies.map((condition) => condition.name) }]
    : [];
  const upcoming = summary.applications
    .filter((application) => application.status === "scheduled")
    .map((application) => ({ kind: "application-upcoming" as const, application }));
  const temperaments = summary.temperaments.length
    ? [{ kind: "temperament" as const, names: summary.temperaments.map((condition) => condition.name) }]
    : [];

  return {
    chips: [...overdue, ...alerts, ...allergies, ...upcoming, ...temperaments],
    alerts,
    notes: summary.daycareHotelNotes?.trim() || null,
    relationships: summary.relationships,
    storedItems: summary.storedItems,
  };
}
